import { useMemo } from "react";
import useAssetNews from "../../hooks/useAssetNews";

const FALLBACK_GRADIENTS = [
  "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
  "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
  "linear-gradient(135deg, #232526 0%, #414345 100%)",
  "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
];

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const truncate = (text, maxLen) => {
  if (!text) return "";
  return text.length > maxLen ? text.slice(0, maxLen) + "..." : text;
};

const NewsCard = ({ article, index }) => {
  const hasImage = !!article.imageUrl;

  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex gap-3 group cursor-pointer min-w-0"
    >
      <div
        className="w-[100px] h-[70px] flex-shrink-0 rounded-lg overflow-hidden"
        style={!hasImage ? { background: FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length] } : undefined}
      >
        {hasImage ? (
          <img
            src={article.imageUrl}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.parentElement.style.background = FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length];
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-400 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-gray-900 leading-tight group-hover:text-purple-600 transition-colors line-clamp-2">
          {truncate(article.title, 80)}
        </h4>
        {article.description && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
            {truncate(article.description, 100)}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          {article.source && (
            <span className="text-xs text-gray-400">{article.source}</span>
          )}
          <span className="text-xs text-gray-400">
            {formatDate(article.publishedAt)}
          </span>
        </div>
      </div>
    </a>
  );
};

const AssetNews = ({ marketId }) => {
  const { articles, loading, error } = useAssetNews(marketId);

  const displayArticles = useMemo(() => articles.slice(0, 4), [articles]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-5 mt-4">
        <h3 className="text-lg font-bold text-gray-900 mb-1">Latest Asset News</h3>
        <p className="text-sm text-gray-500 mb-4">
          Stay updated with the latest news, market movements, and insights related to this asset.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-[100px] h-[70px] flex-shrink-0 rounded-lg bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-1/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || displayArticles.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl p-5 mt-4">
      <h3 className="text-lg font-bold text-gray-900 mb-1">Latest Asset News</h3>
      <p className="text-sm text-gray-500 mb-4">
        Stay updated with the latest news, market movements, and insights related to this asset.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {displayArticles.map((article, i) => (
          <NewsCard key={i} article={article} index={i} />
        ))}
      </div>
    </div>
  );
};

export default AssetNews;
